import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameEventDraft, type GameState } from '../engine/state';
import { bagContains, bagIsCanonical, bagOverlap, bagTotal, type ResourceBag } from '../engine/gameDef';
import type { SeatIdx } from '../types';

// Domestic trading (outline §8; plan §8.2–8.3): the ACTIVE player is party to every
// trade — bystanders deal with the active seat only; gifts and like-for-like are banned;
// executed trades are immediate bilateral card movements with both sides re-checked at
// execution. Offers die on END_TURN (plan §7.6). Only the classic ACTION window trades —
// the paired partner never player-trades (outline §13.2).

export interface TradeOffer {
  id: number;
  proposer: SeatIdx;
  give: ResourceBag;
  get: ResourceBag;
  /** null = open to all (active-seat offers only); bystander offers target the active seat. */
  to: SeatIdx | null;
  acceptedBy: SeatIdx[];
  /** Seats that explicitly passed on this offer. Optional for old replay states. */
  declinedBy?: SeatIdx[];
}

export interface PlayerTradeBag {
  offers: TradeOffer[];
  nextOfferId: number;
}

const tradeBag = (state: GameState): PlayerTradeBag => state.bags['player-trade'] as PlayerTradeBag;

function activeSeat(state: GameState): SeatIdx | null {
  return state.phase.t === 'ACTION' ? state.phase.seat : null;
}

/**
 * Does `seat` have a standing offer that somebody could still answer? (D252) An answer is an
 * acceptance OR a decline, and the seats that can give one are everyone the offer is addressed
 * to who has not resigned. The bot proposer's hold (D215) waits for exactly this: before
 * declines were public it could only wait out the whole consideration window, so a table that
 * had already said no to every offer still sat through eight seconds of nothing.
 */
export function hasUnansweredOffer(state: GameState, seat: SeatIdx): boolean {
  const bag = state.bags['player-trade'] as PlayerTradeBag | undefined;
  if (!bag) return false;
  return bag.offers.some(
    (o) =>
      o.proposer === seat &&
      state.seats.some((s, i) => i !== seat && !s.resigned && (o.to === null || o.to === i) && !o.acceptedBy.includes(i) && !(o.declinedBy ?? []).includes(i)),
  );
}

/** Counterparty solvency — the ONE documented optimistic check (plan §10.3): on a client
 *  projection the counterparty's hand is the unknown-marker stub, and the check passes
 *  optimistically; the server, holding real hands, re-checks at execution (T-RED-010). */
function counterpartyHolds(hand: ResourceBag, bag: ResourceBag): boolean {
  if (hand['__unknown__'] !== undefined) return true;
  return bagContains(hand, bag);
}

/** Execute offer between its proposer and `counterparty` (both sides re-validated). */
function executeOffer(state: GameState, offer: TradeOffer, counterparty: SeatIdx, emit: (e: GameEventDraft) => void): void {
  const a = offer.proposer;
  const b = counterparty;
  for (const [rid, n] of Object.entries(offer.give)) {
    state.seats[a]!.hand[rid] = (state.seats[a]!.hand[rid] ?? 0) - n;
    if (state.seats[a]!.hand[rid] === 0) delete state.seats[a]!.hand[rid];
    state.seats[b]!.hand[rid] = (state.seats[b]!.hand[rid] ?? 0) + n;
  }
  for (const [rid, n] of Object.entries(offer.get)) {
    state.seats[b]!.hand[rid] = (state.seats[b]!.hand[rid] ?? 0) - n;
    if (state.seats[b]!.hand[rid] === 0) delete state.seats[b]!.hand[rid];
    state.seats[a]!.hand[rid] = (state.seats[a]!.hand[rid] ?? 0) + n;
  }
  const bag = tradeBag(state);
  bag.offers = bag.offers.filter((o) => o.id !== offer.id); // exactly once
  emit({ kind: 'TRADE_EXECUTED', vis: 'public', a, b, give: { ...offer.give }, get: { ...offer.get }, offerId: offer.id });
}

export const playerTradeModule: RuleModule = {
  id: 'player-trade',
  register(ctx) {
    ctx.bag({
      init() {
        return { offers: [], nextOfferId: 0 } satisfies PlayerTradeBag;
      },
      clone(bag) {
        const b = bag as PlayerTradeBag;
        return {
          offers: b.offers.map((o) => ({
            id: o.id,
            proposer: o.proposer,
            give: { ...o.give },
            get: { ...o.get },
            to: o.to,
            acceptedBy: [...o.acceptedBy],
            declinedBy: [...(o.declinedBy ?? [])],
          })),
          nextOfferId: b.nextOfferId,
        } satisfies PlayerTradeBag;
      },
    });

    ctx.onTurnBoundary((state) => {
      const bag = tradeBag(state);
      bag.offers = []; // offers die with the turn (plan §7.6)
    });

    ctx.action('OFFER_TRADE', {
      enumerate(state, _env, seat) {
        const active = activeSeat(state);
        if (active === null) return null;
        if (seat !== active && state.seats[seat] === undefined) return null;
        return { descriptor: { open: true } }; // open-bag payload (plan §8.3)
      },
      validate(state, action, env) {
        const active = activeSeat(state);
        if (active === null) return reject('WRONG_PHASE'); // outline §14.5: nothing interposes into 7-resolution
        const give = action.payload['give'];
        const get = action.payload['get'];
        const to = action.payload['to'];
        if (typeof give !== 'object' || give === null || Array.isArray(give)) return reject('BAD_PAYLOAD');
        if (typeof get !== 'object' || get === null || Array.isArray(get)) return reject('BAD_PAYLOAD');
        const giveBag = give as ResourceBag;
        const getBag = get as ResourceBag;
        if (!bagIsCanonical(giveBag) || !bagIsCanonical(getBag)) return reject('BAD_PAYLOAD');
        for (const rid of [...Object.keys(giveBag), ...Object.keys(getBag)]) {
          if (!env.def.resources.some((r) => r.id === rid)) return reject('BAD_PAYLOAD');
        }
        if (bagTotal(giveBag) === 0 || bagTotal(getBag) === 0) return reject('NO_GIFTS'); // outline §8
        if (bagOverlap(giveBag, getBag)) return reject('LIKE_FOR_LIKE'); // outline §8
        if (!bagContains(state.seats[action.seat]!.hand, giveBag)) return reject('CARDS_NOT_HELD');
        if (action.seat === active) {
          if (to !== null && (typeof to !== 'number' || !Number.isInteger(to) || to < 0 || to >= env.seatsCount || to === active)) {
            return reject('BAD_TARGET');
          }
        } else {
          if (to !== active) return reject('ACTIVE_IS_PARTY'); // bystanders deal with the active seat only (outline §8)
        }
        const mine = tradeBag(state).offers.filter((o) => o.proposer === action.seat).length;
        if (mine >= env.cfg.engine.offersPerSeatCap) return reject('OFFER_CAP');
        return OK;
      },
      apply(state, action, _env, emit) {
        const bag = tradeBag(state);
        const offer: TradeOffer = {
          id: bag.nextOfferId,
          proposer: action.seat,
          give: { ...(action.payload['give'] as ResourceBag) },
          get: { ...(action.payload['get'] as ResourceBag) },
          to: action.payload['to'] as SeatIdx | null,
          acceptedBy: [],
          declinedBy: [],
        };
        bag.nextOfferId += 1;
        bag.offers.push(offer);
        emit({ kind: 'TRADE_OFFERED', vis: 'public', seat: action.seat, offerId: offer.id, give: { ...offer.give }, get: { ...offer.get }, to: offer.to });
      },
    });

    ctx.action('RETRACT_TRADE', {
      enumerate(state, _env, seat) {
        if (activeSeat(state) === null) return null;
        return tradeBag(state).offers.filter((o) => o.proposer === seat).map((o) => ({ offerId: o.id }));
      },
      validate(state, action) {
        if (activeSeat(state) === null) return reject('WRONG_PHASE');
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId']);
        if (!offer) return reject('NO_SUCH_OFFER');
        if (offer.proposer !== action.seat) return reject('NOT_YOUR_OFFER');
        return OK;
      },
      apply(state, action, _env, emit) {
        const bag = tradeBag(state);
        const id = Number(action.payload['offerId']);
        bag.offers = bag.offers.filter((o) => o.id !== id);
        emit({ kind: 'TRADE_RETRACTED', vis: 'public', seat: action.seat, offerId: id });
      },
    });

    ctx.action('ACCEPT_TRADE', {
      enumerate(state, _env, seat) {
        const active = activeSeat(state);
        if (active === null) return null;
        return tradeBag(state)
          .offers.filter((o) => {
            if (o.proposer === seat || (o.declinedBy ?? []).includes(seat)) return false;
            if (seat === active) return true; // active accepting a bystander's offer executes
            return o.to === null || o.to === seat; // bystander marks willingness on the active seat's offer
          })
          .map((o) => ({ offerId: o.id }));
      },
      validate(state, action) {
        const active = activeSeat(state);
        if (active === null) return reject('WRONG_PHASE');
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId']);
        if (!offer) return reject('NO_SUCH_OFFER');
        if (offer.proposer === action.seat) return reject('OWN_OFFER');
        // D252: a decline is final for EVERY seat. `enumerate` already hid a declined offer from
        // the active seat as well; validate used to check only the bystander branch, so an
        // active seat's ACCEPT after its own DECLINE was refused by the legal set and accepted
        // by the rules — two answers to one question.
        if ((offer.declinedBy ?? []).includes(action.seat)) return reject('ALREADY_DECLINED');
        if (action.seat === active) {
          // the active seat accepting a bystander offer = execution: both sides re-checked
          if (offer.proposer === active) return reject('OWN_OFFER');
          if (!counterpartyHolds(state.seats[offer.proposer]!.hand, offer.give)) return reject('COUNTERPARTY_SHORT');
          if (!bagContains(state.seats[active]!.hand, offer.get)) return reject('CARDS_NOT_HELD');
        } else {
          if (offer.to !== null && offer.to !== action.seat) return reject('NOT_YOUR_OFFER');
          if (offer.acceptedBy.includes(action.seat)) return reject('ALREADY_ACCEPTED');
        }
        return OK;
      },
      apply(state, action, _env, emit) {
        const active = activeSeat(state)!;
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId'])!;
        if (action.seat === active) {
          executeOffer(state, offer, active, emit); // the active seat's accept IS the ratification (plan §8.2)
        } else {
          offer.acceptedBy.push(action.seat);
          emit({ kind: 'TRADE_ACCEPTED', vis: 'public', seat: action.seat, offerId: offer.id });
        }
      },
    });

    // Declines are public answers, not just a local UI dismissal: a proposer can see that
    // a bot considered an offer and passed while other eligible seats may still accept it.
    ctx.action('DECLINE_TRADE', {
      enumerate(state, _env, seat) {
        const active = activeSeat(state);
        if (active === null) return null;
        return tradeBag(state)
          .offers.filter((o) => {
            if (o.proposer === seat || o.acceptedBy.includes(seat) || (o.declinedBy ?? []).includes(seat)) return false;
            return seat === active ? o.proposer !== active : o.to === null || o.to === seat;
          })
          .map((o) => ({ offerId: o.id }));
      },
      validate(state, action) {
        const active = activeSeat(state);
        if (active === null) return reject('WRONG_PHASE');
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId']);
        if (!offer) return reject('NO_SUCH_OFFER');
        if (offer.proposer === action.seat) return reject('OWN_OFFER');
        if (offer.acceptedBy.includes(action.seat)) return reject('ALREADY_ACCEPTED');
        if ((offer.declinedBy ?? []).includes(action.seat)) return reject('ALREADY_DECLINED');
        if (action.seat !== active && offer.to !== null && offer.to !== action.seat) return reject('NOT_YOUR_OFFER');
        return OK;
      },
      apply(state, action, _env, emit) {
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId'])!;
        (offer.declinedBy ??= []).push(action.seat);
        emit({ kind: 'TRADE_DECLINED', vis: 'public', seat: action.seat, offerId: offer.id });
      },
    });

    ctx.action('RATIFY_TRADE', {
      enumerate(state, _env, seat) {
        const active = activeSeat(state);
        if (active === null || seat !== active) return null;
        return tradeBag(state)
          .offers.filter((o) => o.proposer === seat && o.acceptedBy.length > 0)
          .flatMap((o) => o.acceptedBy.map((w) => ({ offerId: o.id, with: w })));
      },
      validate(state, action) {
        const active = activeSeat(state);
        if (active === null) return reject('WRONG_PHASE');
        if (action.seat !== active) return reject('NOT_YOUR_TURN');
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId']);
        if (!offer) return reject('NO_SUCH_OFFER');
        if (offer.proposer !== active) return reject('NOT_YOUR_OFFER');
        const withSeat = action.payload['with'];
        if (typeof withSeat !== 'number' || !offer.acceptedBy.includes(withSeat)) return reject('NOT_ACCEPTED');
        // execution-time solvency, both sides (plan §8.2)
        if (!bagContains(state.seats[offer.proposer]!.hand, offer.give)) return reject('CARDS_NOT_HELD');
        if (!counterpartyHolds(state.seats[withSeat]!.hand, offer.get)) return reject('COUNTERPARTY_SHORT');
        return OK;
      },
      apply(state, action, _env, emit) {
        const offer = tradeBag(state).offers.find((o) => o.id === action.payload['offerId'])!;
        executeOffer(state, offer, Number(action.payload['with']), emit);
      },
    });
  },
};
